import type { CustomerCardProduct } from '@inktap/contracts/customer-commerce';

export type CardMaterial = 'plastic' | 'wood' | 'metal';

export type CardStyleTheme = {
  backgroundColor: string;
  backgroundImage?: string;
  textColor: string;
  textMutedColor: string;
  accentColor: string;
  accentTextColor: string;
  dividerColor: string;
  slugColor: string;
  overlay?: string;
  overlayOpacity: number;
  shadow?: string;
};

export type CardStyleDefinition = {
  id: string;
  name: string;
  material: CardMaterial;
  finish: string;
  pricing?: Pick<CustomerCardProduct,
    'unitPriceCents' | 'originalUnitPriceCents' | 'currency' | 'pricingToken' | 'promotion'>;
  image: string;
  /** Presentation provenance; never part of a saved card design. */
  imageSource?: 'fallback' | 'catalog';
  description: string;
  theme: CardStyleTheme;
  template: {
    html: string;
    css: string;
  };
};

const BASE_CARD_HTML = (stageClass: string) => `
<div class="card-canvas" data-gjs-type="card-canvas" data-gjs-selectable="false" data-gjs-hoverable="false" data-gjs-draggable="false" data-gjs-droppable="false" data-gjs-copyable="false">
  <div class="card-stage ${stageClass}">
    <div class="card-stage__chip">InkTap NFC</div>
    <div class="card-stage__name">Your Name</div>
    <div class="card-stage__role">Title or Tagline</div>
    <div class="card-stage__divider"></div>
    <div class="card-stage__contact">
      <p>email@inktap.ca</p>
      <p>@socialhandle</p>
      <p>+1 (555) 123-4567</p>
    </div>
    <div class="card-stage__slug">inktap.ca/your-name</div>
  </div>
</div>
`;

const BASE_CARD_CSS = `
body {
  margin: 0;
  font-family: 'Inter', 'Segoe UI', sans-serif;
  background: #0f172a;
  color: #0f172a;
  min-height: 100vh;
}

.card-canvas {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 32px;
}

.card-stage {
  position: relative;
  width: 320px;
  height: 200px;
  border-radius: 24px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--card-background-color, #0ea5e9);
  background-image: var(--card-background-image, linear-gradient(135deg, #0ea5e9, #2563eb));
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: var(--card-text, #ffffff);
  box-shadow: var(--card-shadow, 0 24px 60px rgba(15, 23, 42, 0.48));
  overflow: hidden;
  letter-spacing: 0.01em;
}

.card-stage::before {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--card-overlay, none);
  opacity: var(--card-overlay-opacity, 0.28);
  pointer-events: none;
}

.card-stage__chip {
  align-self: flex-start;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: var(--card-accent, rgba(255, 255, 255, 0.18));
  color: var(--card-accent-text, inherit);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
}

.card-stage__name {
  font-size: 1.45rem;
  font-weight: 700;
  margin: 8px 0 0;
}

.card-stage__role {
  margin: 4px 0 12px;
  font-size: 0.95rem;
  color: var(--card-text-muted, rgba(255, 255, 255, 0.72));
}

.card-stage__divider {
  height: 2px;
  width: 48px;
  border-radius: 999px;
  background: var(--card-divider, rgba(255, 255, 255, 0.45));
}

.card-stage__contact {
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--card-text-muted, rgba(255, 255, 255, 0.75));
}

.card-stage__slug {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--card-slug-text, rgba(255, 255, 255, 0.82));
}
`;

const extractCssVar = (cssVars: string, variable: string): string | undefined => {
  const pattern = new RegExp(`--${variable}\\s*:\\s*([^;]+);`, 'i');
  const match = cssVars.match(pattern);
  if (!match) {
    return undefined;
  }
  return match[1]?.trim();
};

const extractThemeFromCssVars = (cssVars: string): CardStyleTheme => {
  const backgroundColor = extractCssVar(cssVars, 'card-background-color') ?? '#0ea5e9';
  const backgroundImage = extractCssVar(cssVars, 'card-background-image');
  const textColor = extractCssVar(cssVars, 'card-text') ?? '#ffffff';
  const textMutedColor = extractCssVar(cssVars, 'card-text-muted') ?? 'rgba(255, 255, 255, 0.72)';
  const accentColor = extractCssVar(cssVars, 'card-accent') ?? 'rgba(255, 255, 255, 0.18)';
  const accentTextColor = extractCssVar(cssVars, 'card-accent-text') ?? textColor;
  const dividerColor = extractCssVar(cssVars, 'card-divider') ?? textColor;
  const slugColor = extractCssVar(cssVars, 'card-slug-text') ?? textColor;
  const overlay = extractCssVar(cssVars, 'card-overlay');
  const overlayOpacityRaw = extractCssVar(cssVars, 'card-overlay-opacity');
  const overlayOpacity = overlayOpacityRaw ? Number.parseFloat(overlayOpacityRaw) : 0;
  const shadow = extractCssVar(cssVars, 'card-shadow');

  return {
    backgroundColor,
    backgroundImage,
    textColor,
    textMutedColor,
    accentColor,
    accentTextColor,
    dividerColor,
    slugColor,
    overlay,
    overlayOpacity: Number.isFinite(overlayOpacity) ? overlayOpacity : 0,
    shadow
  };
};

const createStyle = ({
  id,
  name,
  material,
  finish,
  image,
  description,
  cssVars
}: {
  id: string;
  name: string;
  material: CardMaterial;
  finish: string;
  image: string;
  description: string;
  cssVars: string;
}): CardStyleDefinition => ({
  id,
  name,
  material,
  finish,
  image,
  imageSource: 'fallback',
  description,
  theme: extractThemeFromCssVars(cssVars),
  template: {
    html: BASE_CARD_HTML(`card-stage--${id}`),
    css: `${BASE_CARD_CSS}
.card-stage.card-stage--${id} {
${cssVars}
}
`
  }
});

export const CARD_STYLES: CardStyleDefinition[] = [
  createStyle({
    id: 'plastic-white',
    name: 'Matte White PVC',
    material: 'plastic',
    finish: 'Matte',
    image: '/cards/plastic-white.svg',
    description: 'Launch-ready matte white PVC layout with strong slug and scan readability.',
    cssVars: `  --card-background-color: #f8fafc;
  --card-background-image: url('/cards/plastic-white.svg');
  --card-text: #0f172a;
  --card-text-muted: rgba(30, 41, 59, 0.72);
  --card-divider: rgba(37, 99, 235, 0.45);
  --card-accent: rgba(37, 99, 235, 0.12);
  --card-accent-text: #1d4ed8;
  --card-slug-text: #1e293b;
  --card-shadow: 0 22px 48px rgba(15, 23, 42, 0.18);
  --card-overlay: linear-gradient(90deg, rgba(37, 99, 235, 0.12), transparent 46%);
  --card-overlay-opacity: 1;`
  }),
  createStyle({
    id: 'plastic-black',
    name: 'Matte Black PVC',
    material: 'plastic',
    finish: 'Matte',
    image: '/cards/plastic-black.svg',
    description: 'Launch-ready matte black PVC layout with high-contrast production-safe text.',
    cssVars: `  --card-background-color: #030405;
  --card-background-image: url('/cards/plastic-black.svg');
  --card-text: #f8fafc;
  --card-text-muted: rgba(226, 232, 240, 0.78);
  --card-divider: rgba(59, 130, 246, 0.55);
  --card-accent: rgba(59, 130, 246, 0.22);
  --card-accent-text: #bfdbfe;
  --card-slug-text: rgba(226, 232, 240, 0.86);
  --card-shadow: 0 28px 60px rgba(0, 0, 0, 0.8);
  --card-overlay: linear-gradient(90deg, rgba(24, 156, 255, 0.1), transparent 48%);
  --card-overlay-opacity: 1;`
  })
];
